const MIN_KEYBOARD_INSET_PX = 80;
const MAX_KEYBOARD_INSET_RATIO = 0.6;
const MIN_KEYBOARD_VIEWPORT_RATIO = 0.35;

const TEXT_INPUT_TYPES = new Set([
  "",
  "email",
  "number",
  "password",
  "search",
  "tel",
  "text",
  "url",
]);

export type VisualKeyboardInsetInput = {
  activeElement: Element | null;
  layoutViewportHeight: number;
  visualViewportHeight: number;
  visualViewportOffsetTop: number;
};

export type VisualKeyboardInsetDebugInfo = VisualKeyboardInsetInput & {
  activeElementDescription: string;
  canActiveElementOpenKeyboard: boolean;
  keyboardInset: number;
  rawInset: number;
  reason: string;
};

export const describeKeyboardFocusElement = (element: Element | null) => {
  if (!(element instanceof HTMLElement)) {
    return "none";
  }

  const segments = [element.tagName.toLowerCase()];

  if (element.id) {
    segments.push(`#${element.id}`);
  }

  if (element.getAttribute("role")) {
    segments.push(`[role="${element.getAttribute("role")}"]`);
  }

  if (element.getAttribute("contenteditable")) {
    segments.push(
      `[contenteditable="${element.getAttribute("contenteditable")}"]`,
    );
  }

  if (element instanceof HTMLInputElement && element.type) {
    segments.push(`[type="${element.type}"]`);
  }

  return segments.join("");
};

export const canElementOpenSoftwareKeyboard = (
  element: Element | null,
): boolean => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const contentEditable = element.getAttribute("contenteditable");

  if (
    element.isContentEditable ||
    (contentEditable !== null && contentEditable.toLowerCase() !== "false")
  ) {
    return true;
  }

  if (element instanceof HTMLTextAreaElement) {
    return !element.disabled && !element.readOnly;
  }

  if (element instanceof HTMLInputElement) {
    return (
      !element.disabled &&
      !element.readOnly &&
      TEXT_INPUT_TYPES.has(element.type)
    );
  }

  return false;
};

export const getVisualKeyboardInsetDebugInfo = ({
  activeElement,
  layoutViewportHeight,
  visualViewportHeight,
  visualViewportOffsetTop,
}: VisualKeyboardInsetInput): VisualKeyboardInsetDebugInfo => {
  const rawInset = Math.max(
    0,
    Math.round(
      layoutViewportHeight - visualViewportHeight - visualViewportOffsetTop,
    ),
  );
  const canActiveElementOpenKeyboard =
    canElementOpenSoftwareKeyboard(activeElement);

  if (rawInset < MIN_KEYBOARD_INSET_PX) {
    return {
      activeElement,
      activeElementDescription: describeKeyboardFocusElement(activeElement),
      canActiveElementOpenKeyboard,
      keyboardInset: 0,
      layoutViewportHeight,
      rawInset,
      reason: "below-minimum-inset",
      visualViewportHeight,
      visualViewportOffsetTop,
    };
  }

  if (!canActiveElementOpenKeyboard) {
    return {
      activeElement,
      activeElementDescription: describeKeyboardFocusElement(activeElement),
      canActiveElementOpenKeyboard,
      keyboardInset: 0,
      layoutViewportHeight,
      rawInset,
      reason: "active-element-cannot-open-keyboard",
      visualViewportHeight,
      visualViewportOffsetTop,
    };
  }

  if (rawInset > layoutViewportHeight * MAX_KEYBOARD_INSET_RATIO) {
    return {
      activeElement,
      activeElementDescription: describeKeyboardFocusElement(activeElement),
      canActiveElementOpenKeyboard,
      keyboardInset: 0,
      layoutViewportHeight,
      rawInset,
      reason: "inset-too-large",
      visualViewportHeight,
      visualViewportOffsetTop,
    };
  }

  if (
    visualViewportHeight <
    layoutViewportHeight * MIN_KEYBOARD_VIEWPORT_RATIO
  ) {
    return {
      activeElement,
      activeElementDescription: describeKeyboardFocusElement(activeElement),
      canActiveElementOpenKeyboard,
      keyboardInset: 0,
      layoutViewportHeight,
      rawInset,
      reason: "viewport-too-small",
      visualViewportHeight,
      visualViewportOffsetTop,
    };
  }

  return {
    activeElement,
    activeElementDescription: describeKeyboardFocusElement(activeElement),
    canActiveElementOpenKeyboard,
    keyboardInset: rawInset,
    layoutViewportHeight,
    rawInset,
    reason: "keyboard",
    visualViewportHeight,
    visualViewportOffsetTop,
  };
};

export const calculateVisualKeyboardInset = (
  input: VisualKeyboardInsetInput,
) => {
  return getVisualKeyboardInsetDebugInfo(input).keyboardInset;
};
